import { createClient } from '@supabase/supabase-js'
import { readCookie, writeCookie, deleteCookie } from '@/lib/cookies'

// Trim whitespace — env vars editadas via dashboard Vercel podem vir com \n
// ou espaços invisíveis no final, o que quebra comparações e concat de URLs.
const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || '').trim()
const supabaseAnonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY || '').trim()

// =====================================================================
// GUARD DE AMBIENTE — canário que impede vazamento PROD↔DEV.
//
// Regra: hostname (-dev em DEV) tem que bater com o Supabase ref do
// bundle. Se um deploy errado colocar bundle PROD em admin-dev.runnaflow.com
// (ou vice-versa), abortamos ANTES de qualquer request, render ou OAuth.
//
// Sem isto, o bug é silencioso: user clica "Entrar com Google" em
// admin-dev.runnaflow.com achando que é DEV, e o OAuth dispara pra
// Supabase PROD porque o bundle tem VITE_SUPABASE_URL = PROD. Esse é
// exatamente o bug reportado em 2026-04-23.
// =====================================================================
const SUPABASE_REF_DEV  = 'kodzwnnwprceuotbbmcz'
const SUPABASE_REF_PROD = 'lwcvlairioyasfstakuh'

if (typeof window !== 'undefined') {
  const host = window.location.hostname
  const isDevHost  = host.endsWith('-dev.runnaflow.com')
  const isProdHost = host === 'runnaflow.com' || host === 'www.runnaflow.com' || host === 'admin.runnaflow.com'
  const bundleIsProd = supabaseUrl.includes(SUPABASE_REF_PROD)
  const bundleIsDev  = supabaseUrl.includes(SUPABASE_REF_DEV)

  if ((isDevHost && bundleIsProd) || (isProdHost && bundleIsDev)) {
    const msg =
      `[runna] ERRO FATAL DE AMBIENTE: hostname=${host} mas Supabase ref no bundle é ` +
      `${bundleIsProd ? 'PROD' : 'DEV'}. Alias do domain está apontando pro deploy errado. ` +
      `Verifique Vercel > Project > Domains: hosts -dev devem ter gitBranch=dev.`
    // Pinta uma tela de erro visível, não só console — login ficaria exposto a OAuth cross-env.
    if (typeof document !== 'undefined') {
      document.documentElement.innerHTML =
        `<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:#0c0c1d;color:#f0f2f5;font-family:system-ui;padding:24px;text-align:center;">
          <div style="max-width:560px;">
            <h1 style="font-size:20px;margin:0 0 12px;color:#ef4444;">Erro de ambiente</h1>
            <p style="font-size:14px;line-height:1.5;color:#c8d0dc;">${msg}</p>
            <p style="font-size:12px;color:#5a6478;margin-top:16px;">Acesso bloqueado para proteger dados. Avise o admin.</p>
          </div>
        </div>`
    }
    throw new Error(msg)
  }
}

/**
 * Storage mirror: localStorage é primário (source of truth). Cookie em
 * `.runnaflow.com` é mirror pra compartilhar sessão entre subdomínios
 * (admin-dev ↔ runnaflow-dev em DEV, admin ↔ runnaflow em PROD).
 *
 * Princípio: se o cookie falhar por qualquer motivo (tamanho, restrição de
 * navegador, CSP), localStorage continua funcionando e a UX não quebra. O
 * cookie é opcional pra otimização cross-subdomain, nunca crítico.
 *
 * Em local dev / Vercel previews (*.vercel.app), cookie não tem domain
 * pai e cai naturalmente em per-origin mode — localStorage só.
 *
 * EXCEÇÃO: aba de impersonação (`?impersonate=TOKEN` ou flag
 * `runna_impers_active_tab`) NÃO espelha em cookie. Se espelhasse, a sessão
 * do target sobrescreveria a sessão do admin no cookie `.runnaflow.com` —
 * a aba admin origem (outro subdomínio) leria cookie no próximo refresh e
 * perderia a sessão admin, caindo pro /login. Isolamento per-origin é
 * essencial aqui, então essas abas ficam só no localStorage.
 */

const COOKIE_MAX_BYTES = 3800 // conservador (limite browser é ~4KB)
const IMPERS_ACTIVE_TAB_KEY = 'runna_impers_active_tab'

// Detecção early: se a URL tem ?impersonate=TOKEN, marca a aba ANTES de
// createClient rodar. Garante que o primeiro getItem do client já pula cookie
// (se não pulasse, o client leria sessão admin do cookie e só trocaria pra
// sessão target depois do verifyOtp — janela de race onde autoRefreshToken
// poderia ler cookie admin e sobrescrever localStorage).
if (typeof window !== 'undefined') {
  try {
    const search = window.location.search
    if (search.includes('impersonate=')) {
      sessionStorage.setItem(IMPERS_ACTIVE_TAB_KEY, '1')
    }
  } catch { /* ignore */ }
}

function isImpersonationTab(): boolean {
  if (typeof sessionStorage === 'undefined') return false
  try { return sessionStorage.getItem(IMPERS_ACTIVE_TAB_KEY) === '1' } catch { return false }
}

// Mirror-storage cross-subdomain precisa max-age = 1 ano pra coincidir com
// refresh-token TTL do Supabase. Helpers em lib/cookies.ts (finding A21).
const SESSION_COOKIE_MAX_AGE = 60 * 60 * 24 * 365 // 1 ano
const writeMirrorCookie = (name: string, value: string) =>
  writeCookie(name, value, SESSION_COOKIE_MAX_AGE)

const mirrorStorage = {
  getItem(key: string): string | null {
    // Aba de impersonação: ignora cookie cross-subdomínio (que pode ter
    // sessão admin). A sessão target dessa aba vive só em localStorage.
    if (isImpersonationTab()) {
      try { return localStorage.getItem(key) } catch { return null }
    }
    // Prioriza cookie (pode trazer sessão de outro subdomínio). Fallback localStorage.
    const c = readCookie(key)
    if (c !== null && c !== '') {
      // Sincroniza pro localStorage (caso tenha vindo via cookie de outro subdomínio)
      try { localStorage.setItem(key, c) } catch { /* ignore */ }
      return c
    }
    try { return localStorage.getItem(key) } catch { return null }
  },
  setItem(key: string, value: string): void {
    // localStorage é PRIMÁRIO: sempre escreve. Source of truth local.
    try { localStorage.setItem(key, value) } catch { /* quota exceeded, etc */ }
    // Aba de impersonação: NÃO espelha em cookie — senão a sessão target
    // sobrescreve a sessão admin no cookie `.runnaflow.com` e a aba admin
    // origem perde a sessão no próximo refresh.
    if (isImpersonationTab()) return
    // Cookie é MIRROR best-effort pra cross-subdomain. Se não couber, só localStorage.
    if (value.length <= COOKIE_MAX_BYTES) {
      writeMirrorCookie(key, value)
    } else {
      // Valor grande: garante que não tem cookie stale que viria primeiro no getItem
      deleteCookie(key)
    }
  },
  removeItem(key: string): void {
    try { localStorage.removeItem(key) } catch { /* ignore */ }
    // Aba de impersonação não mexe no cookie — não apagar sessão admin que
    // outras abas dependem.
    if (isImpersonationTab()) return
    deleteCookie(key)
  },
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    storage: mirrorStorage,
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
})
